import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface Category {
  id: number;
  name: string;
  slug: string;
}

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBlogOpen, setIsBlogOpen] = useState(false);
  const [mobileBlogOpen, setMobileBlogOpen] = useState(false); // ★ MOBILE SUBMENU STATE
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const closeTimeout = useRef<NodeJS.Timeout | null>(null);

  const categories: Category[] = [
    { id: 1, name: 'Analysis', slug: 'analysis' },
    { id: 2, name: 'DeFi', slug: 'defi' },
    { id: 3, name: 'Guide', slug: 'guide' },
    { id: 4, name: 'News', slug: 'news' },
    { id: 5, name: 'Reviews', slug: 'reviews' },
    { id: 6, name: 'Trading', slug: 'trading' },
  ];

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/blog?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsMenuOpen(false);
      setIsSearchOpen(false);
    }
  };

  const handleMouseEnter = () => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
    setIsBlogOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeout.current = setTimeout(() => setIsBlogOpen(false), 1000);
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-100 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 shrink-0">
          <img
            src="http://blog.thecryptotutor.com/wp-content/uploads/2025/11/ChatGPT-Image-15.-Mai-2025_-17_51_48.png"
            alt="Crypto Tutor Logo"
            className="w-14 h-14 rounded-lg object-cover"
          />
          <span className="text-xl font-bold text-gray-900 whitespace-nowrap hidden sm:block">
            The Crypto Tutor
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-8 flex-1 justify-center">
          <Link to="/" className="text-gray-700 hover:text-blue-600 font-medium">Home</Link>

          <div
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 font-medium">
              <span>Blog</span>
              <i className="ri-arrow-down-s-line"></i>
            </button>

            {isBlogOpen && (
              <ul className="absolute left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                {categories.map((cat) => (
                  <li key={cat.id} className="hover:bg-blue-50">
                    <Link
                      to={`/category/${cat.slug}`}
                      className="block px-4 py-2 text-gray-600 hover:text-blue-600"
                    >
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Link to="/crypto-exchanges" className="text-gray-700 hover:text-blue-600 font-medium">Exchanges</Link>
          <Link to="/contact" className="text-gray-700 hover:text-blue-600 font-medium">Contact</Link>
        </nav>

        {/* Desktop Search */}
        <div className="hidden lg:flex items-center">
          <form onSubmit={handleSearchSubmit} className="relative">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            />
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
          </form>
        </div>

        {/* Mobile Buttons */}
        <div className="flex items-center lg:hidden gap-4">

          <button
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            className="text-gray-700 text-2xl"
          >
            <i className="ri-search-line"></i>
          </button>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2 rounded-md text-gray-700"
          >
            <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-3xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Search Panel */}
      {isSearchOpen && (
        <div className="lg:hidden w-full border-t border-gray-200 bg-white px-4 py-3 animate-slideDown">
          <form onSubmit={handleSearchSubmit} className="relative">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-base"
            />
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
          </form>
        </div>
      )}

      {/* MOBILE NAVIGATION */}
      {isMenuOpen && (
        <div className="lg:hidden py-4 border-t border-gray-100 bg-white px-4">
          <nav className="flex flex-col space-y-4">
            <Link to="/" className="text-gray-700 hover:text-blue-600 font-medium">
              Home
            </Link>

            {/* BLOG TOGGLE BUTTON */}
            <button
              onClick={() => setMobileBlogOpen(!mobileBlogOpen)}
              className="flex justify-between items-center w-full text-gray-700 font-medium"
            >
              <span>Blog</span>
              <i
                className={`ri-arrow-${mobileBlogOpen ? 'up' : 'down'}-s-line text-xl`}
              ></i>
            </button>

            {/* BLOG SUBMENU (COLLAPSED BY DEFAULT) */}
            {mobileBlogOpen && (
              <ul className="space-y-1 pl-4">
                {categories.map((cat) => (
                  <li key={cat.id}>
                    <Link
                      to={`/category/${cat.slug}`}
                      className="block px-2 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            <Link to="/crypto-exchanges" className="text-gray-700 hover:text-blue-600 font-medium">
              Exchanges
            </Link>
            <Link to="/contact" className="text-gray-700 hover:text-blue-600 font-medium">
              Contact
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
